import { create } from 'zustand';

import { UPLOAD_PROGRESS_PUBLISH_MS } from '../config/constants';
import type { SharedUploadProgress } from '../types/uploadSharedWorker';

export type UploadRuntimeProgress = SharedUploadProgress;

interface UploadProgressState {
  byUploadTaskId: Readonly<Record<string, UploadRuntimeProgress>>;
}

export const useUploadProgressStore = create<UploadProgressState>(() => ({
  byUploadTaskId: {},
}));

const pending = new Map<string, UploadRuntimeProgress>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const lastPublishedAt = new Map<string, number>();

function publish(id: string, progress: UploadRuntimeProgress): void {
  lastPublishedAt.set(id, Date.now());
  useUploadProgressStore.setState((state) => {
    const current = state.byUploadTaskId[id];
    if (
      current?.stage === progress.stage &&
      current.bytes === progress.bytes &&
      current.totalBytes === progress.totalBytes &&
      current.speedBps === progress.speedBps
    ) {
      return state;
    }
    return { byUploadTaskId: { ...state.byUploadTaskId, [id]: progress } };
  });
}

function flush(id: string): void {
  const timer = timers.get(id);
  if (timer) clearTimeout(timer);
  timers.delete(id);
  const progress = pending.get(id);
  pending.delete(id);
  if (progress) publish(id, progress);
}

function clear(id: string): void {
  const timer = timers.get(id);
  if (timer) clearTimeout(timer);
  timers.delete(id);
  pending.delete(id);
  lastPublishedAt.delete(id);
  useUploadProgressStore.setState((state) => {
    if (!state.byUploadTaskId[id]) return state;
    const byUploadTaskId = { ...state.byUploadTaskId };
    delete byUploadTaskId[id];
    return { byUploadTaskId };
  });
}

/** Imperative bridge for transport callbacks. Each transfer keeps only its newest raw sample and
 * publishes at most four React updates per second; no value in this store is persisted. */
export const uploadProgressActions = {
  report: (id: string, progress: UploadRuntimeProgress): void => {
    pending.set(id, progress);
    if (timers.has(id)) return;
    const elapsed = Date.now() - (lastPublishedAt.get(id) ?? 0);
    const delay = Math.max(0, UPLOAD_PROGRESS_PUBLISH_MS - elapsed);
    if (delay === 0) {
      flush(id);
      return;
    }
    timers.set(
      id,
      setTimeout(() => flush(id), delay),
    );
  },
  clear,
  replace: (byUploadTaskId: Readonly<Record<string, UploadRuntimeProgress>>): void => {
    for (const timer of timers.values()) clearTimeout(timer);
    timers.clear();
    pending.clear();
    lastPublishedAt.clear();
    useUploadProgressStore.setState({ byUploadTaskId: { ...byUploadTaskId } });
  },
  reset: (): void => {
    for (const timer of timers.values()) clearTimeout(timer);
    timers.clear();
    pending.clear();
    lastPublishedAt.clear();
    useUploadProgressStore.setState({ byUploadTaskId: {} });
  },
};
